import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { keccak_256 } from "js-sha3";
import Layout from "@/components/layout/Layout";
import { api, shortHash, timeAgo, formatEth, formatNumber } from "@/lib/api";
import { Wallet, Copy, Check, AlertCircle, Eye, BarChart3, Filter, Download, Server } from "lucide-react";
import HoverTip from "@/components/HoverTip";

// Builder labels — mirror backend BUILDER_NAMES list so we can label known miners
const BUILDER_NAMES = ["quasarbuilder", "Titan Builder", "beaverbuild", "BuilderNet", "Eureka Builder", "rsync-builder"];
function _minerLabel(addr) {
  if (!addr) return null;
  try {
    const last4 = addr.slice(-4);
    const idx = parseInt(last4, 16) % BUILDER_NAMES.length;
    return BUILDER_NAMES[idx];
  } catch { return null; }
}

// Real EIP-55 checksum casing for visual parity with Etherscan
function toChecksumAddr(addr) {
  if (!addr) return "";
  const a = addr.toLowerCase().replace(/^0x/, "");
  const hash = keccak_256(a);
  let out = "0x";
  for (let i = 0; i < a.length; i++) {
    const c = a[i];
    if (/[a-f]/.test(c)) {
      out += parseInt(hash[i], 16) >= 8 ? c.toUpperCase() : c;
    } else {
      out += c;
    }
  }
  return out;
}

function shortChecksum(addr, left = 8, right = 9) {
  const c = toChecksumAddr(addr);
  if (c.length <= left + right + 3) return c;
  return `${c.slice(0, left)}...${c.slice(-right)}`;
}

const InlineCopy = ({ text, size = 12 }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)] ml-1"
      title="Copy"
      data-testid="inline-copy"
    >
      {ok ? <Check size={size} /> : <Copy size={size} />}
    </button>
  );
};

const CopyBtn = ({ text }) => {
  const [ok, setOk] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="ml-2 inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]" data-testid="copy-btn">
      {ok ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

export default function AddressDetail() {
  const { address } = useParams();
  const [data, setData] = useState(null);
  const [contract, setContract] = useState(null);
  const [tab, setTab] = useState("txs");
  const [hideLowValue, setHideLowValue] = useState(false);
  const [inOutFilter, setInOutFilter] = useState("ALL"); // ALL | IN | OUT
  const [tips, setTips] = useState({
    failed: "Warning! Error encountered during contract execution [Out of gas]",
    pending: "Pending",
  });

  useEffect(() => {
    api.get(`/address/${address}`).then((r) => setData(r.data));
    api.get(`/contracts/${address}`).then((r) => setContract(r.data)).catch(() => setContract({verified: false}));
    api.get(`/settings/tooltips`).then((r) => setTips(r.data)).catch(() => {});
  }, [address]);

  const filteredTxs = useMemo(() => {
    if (!data) return [];
    const lowerAddr = data.address.toLowerCase();
    let arr = data.transactions;
    if (hideLowValue) arr = arr.filter((t) => Number(t.value_eth || 0) >= 0.001);
    if (inOutFilter === "IN") arr = arr.filter((t) => t.to_address.toLowerCase() === lowerAddr);
    if (inOutFilter === "OUT") arr = arr.filter((t) => t.from_address.toLowerCase() === lowerAddr);
    return arr;
  }, [data, hideLowValue, inOutFilter]);

  const downloadCsv = () => {
    if (!data) return;
    const header = ["hash", "method", "block", "timestamp_utc", "age", "from", "to", "direction", "value_eth", "txn_fee_eth"];
    const rows = filteredTxs.map((t) => {
      const outgoing = t.from_address.toLowerCase() === data.address.toLowerCase();
      return [
        t.hash, t.method, t.block_number, new Date(t.timestamp * 1000).toISOString(),
        timeAgo(t.timestamp), t.from_address, t.to_address, outgoing ? "OUT" : "IN",
        t.value_eth, t.fee_eth,
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.address.slice(0, 10)}-transactions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <Layout><p>Loading…</p></Layout>;

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-2">
        <Wallet className="text-[var(--ks-primary)]" />
        <h1 className="text-lg font-semibold" data-testid="address-title">Address Details</h1>
      </div>
      <div className="text-sm mono break-all mb-4" data-testid="address-value">
        {data.address}
        <CopyBtn text={data.address} />
      </div>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="ks-stat-tile" data-testid="addr-balance">
          <div className="ks-icon-square">Ξ</div>
          <div>
            <div className="ks-stat-label">ETH Balance</div>
            <div className="ks-stat-value">{formatEth(data.eth_balance, 6)} ETH</div>
          </div>
        </div>
        <div className="ks-stat-tile" data-testid="addr-txcount">
          <div className="ks-icon-square">#</div>
          <div>
            <div className="ks-stat-label">Transactions</div>
            <div className="ks-stat-value">{formatNumber(data.tx_count)}</div>
          </div>
        </div>
        <div className="ks-stat-tile" data-testid="addr-tokens">
          <div className="ks-icon-square">T</div>
          <div>
            <div className="ks-stat-label">Token Holdings</div>
            <div className="ks-stat-value">{data.tokens.length}</div>
          </div>
        </div>
      </section>

      <div className="ks-card">
        <div className="flex border-b border-[var(--ks-border)]">
          <button
            className={`px-4 py-3 text-sm font-medium ${tab === "txs" ? "border-b-2 border-[var(--ks-primary)] text-[var(--ks-primary)]" : "text-[var(--ks-text-soft)]"}`}
            onClick={() => setTab("txs")}
            data-testid="tab-txs"
          >Transactions ({data.transactions.length})</button>
          <button
            className={`px-4 py-3 text-sm font-medium ${tab === "tokens" ? "border-b-2 border-[var(--ks-primary)] text-[var(--ks-primary)]" : "text-[var(--ks-text-soft)]"}`}
            onClick={() => setTab("tokens")}
            data-testid="tab-tokens"
          >Tokens ({data.tokens.length})</button>
          {contract?.verified && (
            <button
              className={`px-4 py-3 text-sm font-medium ${tab === "contract" ? "border-b-2 border-[var(--ks-primary)] text-[var(--ks-primary)]" : "text-[var(--ks-text-soft)]"}`}
              onClick={() => setTab("contract")}
              data-testid="tab-contract"
            >Contract <span className="ks-badge ks-badge-success ml-1" style={{padding:"1px 6px",fontSize:10}}>✓</span></button>
          )}
        </div>

        {tab === "contract" && contract?.verified && (
          <div className="p-4 space-y-4" data-testid="contract-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><div className="ks-stat-label">Contract Name</div><div className="font-medium">{contract.contract_name}</div></div>
              <div><div className="ks-stat-label">Compiler</div><div className="font-medium">{contract.compiler_version}</div></div>
              <div><div className="ks-stat-label">License</div><div className="font-medium">{contract.license}</div></div>
              <div><div className="ks-stat-label">Optimization</div><div className="font-medium">{contract.optimization ? `Yes (${contract.runs} runs)` : "No"}</div></div>
            </div>
            <div>
              <div className="ks-stat-label mb-1">Contract Source Code (Solidity)</div>
              <pre className="bg-[var(--ks-surface)] border border-[var(--ks-border)] rounded p-3 text-xs font-mono overflow-auto max-h-96" data-testid="contract-source">{contract.source_code}</pre>
            </div>
            {contract.abi && contract.abi !== "[]" && (
              <div>
                <div className="ks-stat-label mb-1">Contract ABI</div>
                <pre className="bg-[var(--ks-surface)] border border-[var(--ks-border)] rounded p-3 text-xs font-mono overflow-auto max-h-40" data-testid="contract-abi">{contract.abi}</pre>
              </div>
            )}
            <div className="text-xs text-[var(--ks-text-soft)]">Read/Write UI arrives in the next update.</div>
          </div>
        )}

        {tab === "txs" && (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--ks-border)] flex-wrap gap-2" data-testid="addr-txs-header">
              <div className="flex items-center gap-2 text-xs text-[var(--ks-text-soft)]">
                <span>↓↑</span>
                <span>Latest {filteredTxs.length} from a total of <Link to="/txs" className="ks-link font-medium">{formatNumber(data.tx_count)}</Link> transactions</span>
                <BarChart3 size={13} className="text-[var(--ks-text-soft)]" />
              </div>
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="hide-low-value">
                  <span className="text-[var(--ks-text-soft)] uppercase tracking-wide">Hide Low Value</span>
                  <span
                    role="switch"
                    aria-checked={hideLowValue}
                    onClick={() => setHideLowValue((v) => !v)}
                    className={`relative inline-block w-9 h-5 rounded-full transition-colors ${hideLowValue ? "bg-[var(--ks-primary)]" : "bg-slate-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${hideLowValue ? "translate-x-4" : ""}`}></span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1.5 border border-[var(--ks-border)] rounded-md px-2.5 py-1 hover:bg-[var(--ks-surface)]"
                  data-testid="download-csv"
                >
                  <Download size={12} /> Download Page Data
                </button>
                <div className="relative group">
                  <button className="inline-flex items-center gap-1 border border-[var(--ks-border)] rounded-md px-2 py-1 hover:bg-[var(--ks-surface)]" data-testid="inout-filter-btn">
                    <Filter size={11} />
                    <span className="text-[var(--ks-text-soft)]">{inOutFilter === "ALL" ? "ANY" : inOutFilter}</span>
                  </button>
                  <div className="hidden group-hover:block absolute right-0 top-full mt-1 bg-white border border-[var(--ks-border)] rounded-md shadow-lg py-1 min-w-[100px] z-10">
                    {["ALL", "IN", "OUT"].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setInOutFilter(v)}
                        className={`block w-full text-left px-3 py-1.5 hover:bg-[var(--ks-surface)] ${inOutFilter === v ? "text-[var(--ks-primary)] font-medium" : ""}`}
                        data-testid={`inout-filter-${v}`}
                      >
                        {v === "ALL" ? "ANY" : v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="ks-table">
                <thead>
                  <tr className="text-[var(--ks-text-soft)] text-xs">
                    <th className="w-8"></th>
                    <th>Transaction Hash</th>
                    <th>Method</th>
                    <th>Block</th>
                    <th>Age</th>
                    <th>From</th>
                    <th className="text-center">
                      <span className="inline-flex items-center gap-1"><Filter size={10}/> ANY</span>
                    </th>
                    <th>To</th>
                    <th className="text-right">Amount</th>
                    <th className="text-right">Txn Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxs.map((t) => {
                    const outgoing = t.from_address.toLowerCase() === data.address.toLowerCase();
                    const counterparty = outgoing ? t.to_address : t.from_address;
                    const fromLabel = _minerLabel(t.from_address);
                    return (
                      <tr key={t.hash} data-testid={`addr-tx-${t.hash}`}>
                        <td>
                          <Link to={`/tx/${t.hash}`} className="text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]" title="View transaction">
                            <Eye size={14} />
                          </Link>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            {t.status === "Failed" && (
                              <HoverTip label={tips.failed}>
                                <span className="inline-flex items-center text-red-600 cursor-help" data-testid={`fail-icon-${t.hash}`}>
                                  <AlertCircle size={14} strokeWidth={2.5} />
                                </span>
                              </HoverTip>
                            )}
                            {t.status === "Pending" && (
                              <HoverTip label={tips.pending}>
                                <span className="inline-flex items-center text-amber-500 cursor-help" data-testid={`pending-icon-${t.hash}`}>
                                  <AlertCircle size={14} strokeWidth={2.5} />
                                </span>
                              </HoverTip>
                            )}
                            <Link to={`/tx/${t.hash}`} className="ks-link mono">
                              {t.hash.slice(0, 10)}…
                            </Link>
                            <InlineCopy text={t.hash} />
                          </div>
                        </td>
                        <td>
                          <span className="inline-block border border-[var(--ks-border)] rounded-md px-2 py-0.5 text-xs bg-[var(--ks-surface)] text-[var(--ks-text)]">
                            {t.method}
                          </span>
                        </td>
                        <td>
                          <Link to={`/block/${t.block_number}`} className="ks-link">{formatNumber(t.block_number)}</Link>
                        </td>
                        <td className="whitespace-nowrap text-[var(--ks-text-soft)]">{timeAgo(t.timestamp)}</td>
                        <td className="whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <Link to={`/address/${t.from_address}`} className="mono text-xs text-[var(--ks-text)] hover:text-[var(--ks-primary)]">
                              {shortChecksum(t.from_address, 8, 6)}
                            </Link>
                            <InlineCopy text={t.from_address} />
                          </div>
                        </td>
                        <td className="text-center">
                          <span
                            className={`inline-block rounded-md px-2 py-0.5 text-[10px] font-semibold tracking-wide ${outgoing ? "bg-orange-50 text-orange-600 border border-orange-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
                            title={outgoing ? "Outgoing" : "Incoming"}
                            data-testid={`inout-badge-${t.hash}`}
                          >
                            {outgoing ? "OUT" : "IN"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <Link
                              to={`/address/${t.to_address}`}
                              className="mono text-xs text-[var(--ks-text)] hover:text-[var(--ks-primary)]"
                            >
                              {shortChecksum(t.to_address, 8, 9)}
                            </Link>
                            <InlineCopy text={t.to_address} />
                          </div>
                        </td>
                        <td className="text-right whitespace-nowrap">{formatEth(t.value_eth, 8)} ETH</td>
                        <td className="text-right whitespace-nowrap text-[var(--ks-text-soft)]">{formatEth(t.fee_eth, 8)}</td>
                      </tr>
                    );
                  })}
                  {filteredTxs.length === 0 && (
                    <tr><td colSpan={10} className="text-center py-8 text-[var(--ks-text-soft)]">No transactions match the current filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "tokens" && (
          <div className="overflow-x-auto">
            <table className="ks-table">
              <thead>
                <tr><th>Token</th><th>Type</th><th>Contract</th><th className="text-right">Balance</th></tr>
              </thead>
              <tbody>
                {data.tokens.map((t) => (
                  <tr key={t.contract} data-testid={`addr-token-${t.symbol}`}>
                    <td>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-[var(--ks-text-soft)]">{t.symbol}</div>
                    </td>
                    <td><span className="ks-badge">{t.type}</span></td>
                    <td className="mono text-xs">{shortHash(t.contract, 10, 8)}</td>
                    <td className="text-right font-medium">
                      {typeof t.balance === "number" ? t.balance.toLocaleString() : t.balance} {t.symbol}
                    </td>
                  </tr>
                ))}
                {data.tokens.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-[var(--ks-text-soft)]">No tokens held.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
